// Must run before any library (PostHog, Sentry) that may JSON.stringify cyclic objects.
// See src/lib/safeJsonStringify.ts for details.
import { installSafeJsonStringify } from "./lib/safeJsonStringify";

installSafeJsonStringify();

import * as Sentry from "@sentry/react";
import { config } from "./config";

// Performance tracing runs on deployed environments only. MODE comes from the
// build command (`vite build --mode staging` / `--mode production`), so this is
// decided at build time and does not depend on SSM/env values. The dev server
// runs with MODE === "development", so `pnpm dev` sends no transactions.
const isTracingEnabled =
	import.meta.env.MODE === "staging" || import.meta.env.MODE === "production";

Sentry.init({
	dsn: "https://d6ffb2f4fa2e52b1c195391268b1c2e2@o170281.ingest.us.sentry.io/4512095839911936",
	environment:
		config.sentry.environment || import.meta.env.MODE || "development",
	ignoreErrors: [
		"Assignment to constant variable.",
		"Attempted to assign to readonly property.",
		"Network Error",
		/Loading CSS chunk.*/,
		/Non-Error promise rejection captured.*/,
	],
	// Keep 10% on staging/prod. Sentry's quickstart suggests 1.0, which would
	// send a transaction for every pageload and every navigation from every
	// user. 0 locally, so `pnpm dev` never sends transactions.
	tracesSampleRate: isTracingEnabled ? 0.1 : 0,
	// Which outgoing requests get `sentry-trace` / `baggage` headers attached.
	// Same-origin only: adding them to a cross-origin API requires that backend
	// to list both headers in Access-Control-Allow-Headers, and until it does
	// the CORS preflight fails and the request breaks outright.
	//
	// Sentry's default also includes "localhost", which is NOT safe here: local
	// dev runs the API on http://localhost:3004 while Vite serves on :3000, so
	// that entry would attach the headers cross-origin and could break every
	// local API call. Add specific origins here only once that backend accepts
	// the two headers.
	//
	// Frontend spans (pageload, navigation, fetch timing) do not depend on this
	// — only the link from a frontend span to the backend trace does.
	tracePropagationTargets: [/^\//],
});

import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import * as TanStackQueryProvider from "./integrations/tanstack-query/root-provider.tsx";
import {
	isChunkLoadError,
	reloadForChunkError,
	reportChunkLoadError,
} from "./lib/chunkReload";

import { routeTree } from "./routeTree.gen";

// A deploy that landed while the app was open (or idle) leaves it referencing
// chunk hashes the CDN no longer serves. Vite fires `vite:preloadError` when
// such a preload/import fails; recover by reloading to the fresh index.html.
// Applies everywhere — the Flex webview hits this most, but web has the same
// hashed-chunk problem.
//
// Do NOT call event.preventDefault() here. A cancelled event makes Vite resolve
// the failed import with `undefined`; TanStack's lazyRouteComponent then reads
// `res["default"]` off undefined and throws
//   TypeError: undefined is not an object (evaluating 'E[t??"default"]')
// which is exactly the error this branch was chasing in Sentry. Letting the
// real "Failed to fetch dynamically imported module" error propagate keeps
// TanStack's built-in stale-chunk reload and the errorComponent recovery
// (isChunkLoadError) working, and keeps the real message in Sentry.
window.addEventListener("vite:preloadError", (event) => {
	// Vite attaches the underlying import error as `payload`.
	const payload = (event as Event & { payload?: unknown }).payload;
	reportChunkLoadError(
		payload ?? new Error("vite:preloadError"),
		"vite:preloadError",
	);
	reloadForChunkError();
});

// Safety net: catch stale-chunk TypeErrors that slip past React/Router error
// boundaries (e.g. errors inside TanStack Router's internal lazy loader that
// don't propagate to errorComponent). If it looks like a chunk error, reload.
//
// Both handlers only call preventDefault() when a reload actually started.
// reloadForChunkError() returns false once its guard trips (we already
// reloaded moments ago and are still failing — a genuinely broken deploy);
// swallowing the error in that case would hide it from Sentry and leave the
// user on a blank screen with nothing retrying.
window.addEventListener("error", (event) => {
	if (isChunkLoadError(event.error)) {
		reportChunkLoadError(event.error, "window.error");
		if (reloadForChunkError()) event.preventDefault();
	}
});

// A failed dynamic import that nothing awaited (e.g. a router preload on
// intent) surfaces only as an unhandled rejection, never reaching an error
// boundary. Recover from those too.
window.addEventListener("unhandledrejection", (event) => {
	if (isChunkLoadError(event.reason)) {
		reportChunkLoadError(event.reason, "window.unhandledrejection");
		if (reloadForChunkError()) event.preventDefault();
	}
});

import "non.geist";
import "non.geist/mono";
import "./styles.css";

const TanStackQueryProviderContext = TanStackQueryProvider.getContext();
const router = createRouter({
	routeTree,
	context: {
		...TanStackQueryProviderContext,
		user: null,
		agent: null,
		partner: null,
		partners: [],
		hasMultiplePartners: false,
		isAgentAccess: false,
	},
	defaultPreload: "intent",
	scrollRestoration: true,
	defaultStructuralSharing: true,
	defaultPreloadStaleTime: 0,
});

// Performance tracing. Installed here rather than in Sentry.init above because
// tanstackRouterBrowserTracingIntegration needs the router instance: it names
// transactions by route pattern (/flex/partner/$id) instead of raw URL, so all
// the ids collapse into one transaction rather than one per id.
//
// Sentry.init deliberately stays at the top of this file so errors are captured
// as early as possible. Adding the integration here loses nothing: the pageload
// span is reconstructed from the browser performance timeline, not from the
// moment the integration was installed.
if (isTracingEnabled) {
	Sentry.addIntegration(Sentry.tanstackRouterBrowserTracingIntegration(router));
}

// Register the router instance for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

/**
 * Last-resort boundary, outside the router. A stale chunk that escapes the
 * route-level error components lands here, so it gets the same reload recovery
 * instead of a dead-end message.
 */
function RootErrorFallback({ error }: { error: unknown }) {
	if (isChunkLoadError(error) && reloadForChunkError()) {
		return <p>Updating…</p>;
	}
	return <p>Something went wrong.</p>;
}

const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(
		<StrictMode>
			<Sentry.ErrorBoundary
				fallback={({ error }) => <RootErrorFallback error={error} />}
				showDialog={false}
			>
				<TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
					<RouterProvider router={router} />
				</TanStackQueryProvider.Provider>
			</Sentry.ErrorBoundary>
		</StrictMode>,
	);
}
